import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  modifyTemplateMealOptionFoods,
  modifyTemplateMealOptions,
  modifyTargetPlanMealOptionFoods,
  fetchTargetPlanDetails,
  modifyTargetPlanMealOptions,
} from "../api/dietApi";
import { useTemplateEventTypeId, useRecipeEventTypeId } from "../eventTypes";
import { fetchRecipesByIdMap } from "../recipes/recipeCatalog";
import { normalizeKey } from "../utils/normalizeDietKey";
import {
  getMealFoodId,
  getRecipeCatalogId,
  getRecipeDetails,
  getRecipeName,
} from "./recipeId";
import {
  cacheRecipeDetail,
  ensureRecipeDetailsForRows,
  fetchMissingRecipeDetails,
} from "./recipeDetailsCache";
import { findOptionId, optionNumber } from "../utils/formatOptionTitle";
import { recipeCatalogToMealDetail } from "./recipeCatalogToMealDetail";
import {
  resolveUnitName,
  resolveScaledNutrition,
} from "../nutrients/calculateMealNutrients";
import { getBaseNutrient, nutrientsToDisplayObject } from "../nutrients/nutrientDisplay";
import { formatIntakeTimeForApi } from "./intakeTime";
import { mergeOptimisticRows } from "./mergeOptimisticRows";

// Map a unit label ("g", "serving", "bowl", etc.) to a quantity_type_id from the canonical list (dietCommonUtils).
// Use normalized comparison so "Bowl" matches "bowl". Only IDs from quantityTypes (diets API) are valid.
const matchQuantityTypeId = (unitKey, quantityTypes = []) => {
  if (!unitKey || !quantityTypes?.length) return null;
  const keyNorm = String(unitKey).trim().toLowerCase();
  const hit = quantityTypes.find((qt) => {
    const d = (qt?.description ?? "").trim().toLowerCase();
    const n = (qt?.name ?? "").trim().toLowerCase();
    return d === keyNorm || n === keyNorm;
  });
  return hit?.id || null;
};

// Build a quick lookup of unit label -> quantity_type_id for a given food detail
const buildUnitToIdMap = (detail, quantityTypes = []) => {
  const map = {};
  if (!detail?.measures) return map;

  detail.measures.forEach((m) => {
    const unitLabel = m.unit_option_name || m.unit_name;
    if (!unitLabel) return;
    const id = matchQuantityTypeId(unitLabel, quantityTypes);
    if (id) map[unitLabel] = id;
  });

  return map;
};

const detailToFoodDetails = (detail) => ({
  main_details: {
    food_name: getRecipeName(detail),
    common_names: detail.common_names || "",
    calories_calculated_for: detail.calories_calculated_for || 100,
  },
  nutrition: nutrientsToDisplayObject(detail?.nutrients),
  measures: (detail.measures || []).map((m) => ({
    unit_option_name: m.unit_option_name,
    basic_unit_measure: m.basic_unit_measure || 1,
    serving_size_unit: m.serving_size_unit || "g",
  })),
});

async function handleQuantityTypeApiError(res, clearDietCommonUtilsCache, fetchDietCommonUtils) {
  const msg = (res?.message || "").toLowerCase();
  if (!res?.success && (msg.includes("quantity type") || msg.includes("quantity_type"))) {
    clearDietCommonUtilsCache?.();
    await fetchDietCommonUtils?.();
  }
}

export default function useDietPlanStructureEditor({
  periodName,
  foodData,
  mealOptionIds, // { "Option 1": <uuid>, ... }
  dietCommonUtils,
  fetchDietCommonUtils,
  clearDietCommonUtilsCache, // optional: clear cache when API says quantity type not found
  setAlertMessage,
  setAlertType,
  context = "template", // NEW: "template" or "plan"
  targetPlanId: _targetPlanId = null, // NEW: for plan context
  eventTypeId: eventTypeIdProp = null,
  clientId: _clientId = null,
}) {
  const templateEventTypeId = useTemplateEventTypeId();
  const recipeCatalogEventTypeId = useRecipeEventTypeId();
  const eventTypeId = eventTypeIdProp ?? templateEventTypeId;
  const [savedFoodItems, setSavedFoodItems] = useState({});
  const [foodState, setFoodState] = useState({
    activeFoodItem: {},
    gramsInputValue: "",
    timeValue: "",
    quantityType: "",
    availableQuantityTypes: [],
  });

  const loadedOptionsRef = useRef(new Set());
  const foodsLoadGenRef = useRef(0);

  // Server ids we have successfully deleted. A DELETE that has returned 200 is
  // not necessarily visible to the next GET (read-after-write lag, or a fetch
  // that was already in flight), and by then the row is gone from local state —
  // so nothing would stop the stale server copy reappearing as a card. Rows
  // listed here are treated as absent until a fetch confirms they really are.
  const deletedRowsRef = useRef(new Map()); // serverId -> expiry timestamp
  const DELETED_ROW_TTL_MS = 30_000;

  const rememberDeleted = useCallback((serverId) => {
    if (serverId == null) return;
    deletedRowsRef.current.set(String(serverId), Date.now() + DELETED_ROW_TTL_MS);
  }, []);

  /**
   * Drop rows the user has just deleted, and retire tombstones that have done
   * their job. A tombstone clears as soon as a fetch comes back without the row
   * (deletion confirmed); the TTL is only a backstop so a row the server truly
   * still has can reappear instead of being hidden forever.
   */
  const dropDeletedRows = useCallback((cardRows) => {
    const tombstones = deletedRowsRef.current;
    if (!tombstones.size) return cardRows;

    const now = Date.now();
    const present = new Set(cardRows.map((r) => String(r.deleteId ?? r.id)));
    tombstones.forEach((expiry, id) => {
      if (!present.has(id) || expiry <= now) tombstones.delete(id);
    });
    if (!tombstones.size) return cardRows;
    return cardRows.filter((r) => !tombstones.has(String(r.deleteId ?? r.id)));
  }, []);

  useEffect(() => {
    if (!dietCommonUtils) fetchDietCommonUtils?.();
  }, [dietCommonUtils, fetchDietCommonUtils]);

  const mapUnitToQuantityTypeId = useCallback(
    (unitKey, unitToIdMap) => {
      if (!unitKey) return null;

      // Prefer explicit mapping from the loaded food details
      if (unitToIdMap && unitToIdMap[unitKey]) {
        return unitToIdMap[unitKey];
      }

      const qtyTypes = dietCommonUtils?.quantity_types || [];
      const matched = matchQuantityTypeId(unitKey, qtyTypes);
      return matched || null;
    },
    [dietCommonUtils]
  );

  // --- ensure the selected option has a placeholder array so UI clears immediately ---
  const ensureOptionPlaceholder = useCallback((optionTab) => {
    if (!optionTab) return;
    setSavedFoodItems((prev) =>
      prev[optionTab] ? prev : { ...prev, [optionTab]: [] }
    );
  }, []);

  // ---------- load items for an option ----------

  const buildCardRowFromFood = useCallback(
    (r, detailsMap) => {
      const key = getMealFoodId(r);
      if (!key) return null;

      const detail =
        getRecipeDetails(r) || detailsMap?.get(String(key)) || null;
      const quantity = Number(r.quantity ?? 1);
      const unitName = resolveUnitName(r, detail, dietCommonUtils);
      const scaled = resolveScaledNutrition(r, detail, quantity, unitName);
      const unitToIdMap = buildUnitToIdMap(
        detail,
        dietCommonUtils?.quantity_types
      );
      if (unitName && r.quantity_type_id && !unitToIdMap[unitName]) {
        unitToIdMap[unitName] = r.quantity_type_id;
      }
      const quantityTypeId =
        mapUnitToQuantityTypeId(unitName, unitToIdMap) ||
        r.quantity_type_id ||
        null;

      const serverId = r.template_food_id || r.plan_food_id || r.id;
      if (serverId == null) return null;

      return {
        deleteId: serverId,
        id: r.id || r.template_food_id || r.plan_food_id,
        localId: String(serverId),
        food_id: key,
        food_name: getRecipeName(detail) || getRecipeName(r) || `Recipe ${String(key).slice(0, 8)}`,
        common_name: detail?.common_names || r.common_name || "",
        recipe_details: detail || undefined,
        food_details: detail ? detailToFoodDetails(detail) : r.food_details,
        scaled_nutrition: scaled,
        quantity,
        quantity_type: unitName || r.quantity_type || null,
        quantity_type_id: quantityTypeId,
        unitToIdMap,
        intake_time: r.intake_time || null,
        protein: scaled.proteins ?? scaled.protein,
        carbs: scaled.carbs,
        fats: scaled.fats,
        fiber: scaled.fiber,
        sugar: scaled.sugars ?? scaled.sugar,
        calories: scaled.calories,
        // Deliberately no `_updated` stamp: this rebuilds a row from data we
        // already had, which is not a user edit. FoodItemCard flashes a green
        // ring for 3s when `_updated` is fresh, and stamping here made EVERY
        // card in the option flash whenever any one of them changed.
        // mergeOptimisticRows carries the real edit timestamp forward.
      };
    },
    [dietCommonUtils, mapUnitToQuantityTypeId]
  );

  const loadOptionFromTemplateOnce = useCallback(
    async (selectedOptionTab, foodDataOverride = null) => {
      if (!selectedOptionTab) return [];
      const generation = ++foodsLoadGenRef.current;

      const sourceFoods = foodDataOverride ?? foodData;
      const optionKey = normalizeKey(selectedOptionTab || "");
      const optionKeyNum =
        String(selectedOptionTab).match(/\d+/)?.[0] || optionKey;
      const periodKey = normalizeKey(periodName);

      const rows = (sourceFoods || []).filter((item) => {
        const mealTypeMatch = normalizeKey(item.meal_type) === periodKey;

        const rawOpt = String(item.option || "1");
        const optNorm = normalizeKey(rawOpt);
        const optNum = rawOpt.match(/\d+/)?.[0];

        const optionMatch =
          optNorm === optionKey || (optNum && optNum === optionKeyNum);

        return mealTypeMatch && optionMatch;
      });

      // if no rows yet, keep any pending optimistic items but no server rows
      if (rows.length === 0) {
        if (generation !== foodsLoadGenRef.current) return [];
        setSavedFoodItems((prev) => ({
          ...prev,
          [selectedOptionTab]: mergeOptimisticRows(prev[selectedOptionTab] || [], []),
        }));
        return [];
      }

      const detailsMap = await ensureRecipeDetailsForRows(rows, recipeCatalogEventTypeId);
      if (generation !== foodsLoadGenRef.current) return [];

      const cardRows = dropDeletedRows(
        rows.map((r) => buildCardRowFromFood(r, detailsMap)).filter(Boolean)
      );

      setSavedFoodItems((prev) => ({
        ...prev,
        [selectedOptionTab]: mergeOptimisticRows(prev[selectedOptionTab] || [], cardRows),
      }));
      loadedOptionsRef.current.add(selectedOptionTab);

      return cardRows;
    },
    [foodData, periodName, recipeCatalogEventTypeId, buildCardRowFromFood, dropDeletedRows]
  );
  // Refresh plan foods for target plans. `optionTabs` may be a single title or
  // an array — one GET, then merge each requested option into savedFoodItems.
  const refreshPlanFoods = useCallback(
    async (planId, optionTabs, userId) => {
      if (!planId || context !== "plan") return null;

      const tabs = (Array.isArray(optionTabs) ? optionTabs : [optionTabs]).filter(
        Boolean
      );
      if (!tabs.length) return null;
      const generation = ++foodsLoadGenRef.current;

      try {
        const result = await fetchTargetPlanDetails(
          eventTypeId,
          planId,
          userId
        );
        if (generation !== foodsLoadGenRef.current) return null;
        if (result.success && result.data?.data) {
          const planData = result.data.data;

          const foods = [];
          if (planData.meals) {
            planData.meals.forEach((meal) => {
              meal.meal_options?.forEach((option) => {
                option.foods?.forEach((food) => {
                  foods.push({
                    ...food,
                    meal_type: meal.meal_type_description || periodName,
                    option: option.title || tabs[0],
                  });
                });
              });
            });
          }

          const detailsMap = await ensureRecipeDetailsForRows(
            foods,
            recipeCatalogEventTypeId
          );
          if (generation !== foodsLoadGenRef.current) return planData;

          const periodKey = normalizeKey(periodName);

          setSavedFoodItems((prev) => {
            const next = { ...prev };
            tabs.forEach((tab) => {
              const optionKey = normalizeKey(tab || "");
              const optionKeyNum = String(tab).match(/\d+/)?.[0] || optionKey;
              const rows = foods.filter((item) => {
                const mealTypeMatch = normalizeKey(item.meal_type) === periodKey;
                const rawOpt = String(item.option || "1");
                const optNorm = normalizeKey(rawOpt);
                const optNum = rawOpt.match(/\d+/)?.[0];
                const optionMatch =
                  optNorm === optionKey || (optNum && optNum === optionKeyNum);
                return mealTypeMatch && optionMatch;
              });
              const cardRows = dropDeletedRows(
                rows.map((r) => buildCardRowFromFood(r, detailsMap)).filter(Boolean)
              );
              next[tab] = mergeOptimisticRows(prev[tab] || [], cardRows);
            });
            return next;
          });
          return planData;
        }
      } catch (error) {
        console.error("Error refreshing plan foods:", error);
      }
      return null;
    },
    [context, eventTypeId, periodName, recipeCatalogEventTypeId, buildCardRowFromFood, dropDeletedRows]
  );

  const fetchDetailIfNeeded = useCallback(async (recipe) => {
    const recipeId = getMealFoodId(recipe) || getRecipeCatalogId(recipe);
    if (!recipeId) return null;
    const inlineDetail = getRecipeDetails(recipe);
    if (inlineDetail) return { ...inlineDetail, recipe_id: recipeId };

    const [cached] = await fetchMissingRecipeDetails([recipeId]);
    if (cached) return cached;

    const hasFullCatalog =
      Array.isArray(recipe.nutrient_composition) && recipe.nutrient_composition.length > 0;
    let catalogRecipe = hasFullCatalog ? recipe : null;

    if (!catalogRecipe && recipeCatalogEventTypeId) {
      const res = await fetchRecipesByIdMap(recipeCatalogEventTypeId, [recipeId]);
      catalogRecipe = res.recipesById?.[String(recipeId)] ?? null;
    }

    const fromCatalog = recipeCatalogToMealDetail(catalogRecipe || recipe);
    if (fromCatalog) {
      cacheRecipeDetail(recipeId, fromCatalog);
      return fromCatalog;
    }
    return null;
  }, [recipeCatalogEventTypeId]);

  const normalizeDetailForModal = useCallback(
    (detail) => {
      const getVal = (key) => getBaseNutrient(detail, key);
      const unitToIdMap = buildUnitToIdMap(
        detail,
        dietCommonUtils?.quantity_types
      );
      // Only show units that have a linked quantity_type_id (from diets API)
      const linkedUnits = Object.keys(unitToIdMap);
      const fallbackToG = matchQuantityTypeId("g", dietCommonUtils?.quantity_types);
      const availableQuantityTypes =
        linkedUnits.length > 0 ? linkedUnits : fallbackToG ? ["g"] : [];
      if (availableQuantityTypes.length === 1 && availableQuantityTypes[0] === "g" && fallbackToG) {
        unitToIdMap["g"] = fallbackToG;
      }

      const conversions = (detail?.measures || []).reduce((acc, m) => {
        acc[m.unit_option_name] = {
          value: m.basic_unit_measure || 1,
          unit: m.serving_size_unit || "g",
        };
        return acc;
      }, {});
      if (availableQuantityTypes.includes("g") && !conversions["g"]) {
        conversions["g"] = { value: 1, unit: "g" };
      }

      return {
        ...detail,
        recipe_id: getMealFoodId(detail),
        food_name: getRecipeName(detail),
        common_name: detail.common_names || "",
        protein: getVal("proteins"),
        carbs: getVal("carbs"),
        fats: getVal("fats"),
        fiber: getVal("fiber"),
        sugar: getVal("sugars"),
        calories: getVal("calories"),
        calories_calculated_for: detail.calories_calculated_for || 100,
        quantity_conversions: {
          conversions,
        },
        per_quantity: 1,
        unitToIdMap,
        availableQuantityTypes,
      };
    },
    [dietCommonUtils]
  );
  // Add this new function in the diet plan structure editor.
  const refreshTemplateFoods = useCallback(
    async (selectedOptionTab) => {
      if (context !== "template") return;

      // Clear the loaded flag to force reload
      loadedOptionsRef.current.delete(selectedOptionTab);

      // Reload the foods for this option
      const loadedItems = await loadOptionFromTemplateOnce(selectedOptionTab);

      return loadedItems;
    },
    [context, loadOptionFromTemplateOnce]
  );

  // ---------- calculate nutrition ----------
  const calculateNutritionalValues = useCallback(
    (quantity, item, quantityType) => {
      const parsed = parseFloat(quantity);
      if (!item || !parsed || isNaN(parsed)) {
        return {
          protein: "0.00",
          carbs: "0.00",
          fats: "0.00",
          fiber: "0.00",
          sugar: "0.00",
          calories: "0.00",
        };
      }
      const conv = item.quantity_conversions?.conversions?.[quantityType];
      const unitGrams = conv?.value || 1;
      const totalGrams = parsed * unitGrams;
      const baseGrams = item.calories_calculated_for || 100;
      const scale = (value) =>
        (((Number(value) || 0) * totalGrams) / baseGrams).toFixed(2);
      return {
        protein: scale(item.protein),
        carbs: scale(item.carbs),
        fats: scale(item.fats),
        fiber: scale(item.fiber),
        sugar: scale(item.sugar),
        calories: scale(item.calories),
      };
    },
    []
  );

  // ---------- mutations ----------
  const addFood = useCallback(
    async ({ selectedOptionTab, preconfiguredFood = null, silent = false }) => {
      // Support preconfigured food for duplication
      let activeFoodItem, gramsInputValue, timeValue, quantityType;
      
      if (preconfiguredFood) {
        activeFoodItem = preconfiguredFood;
        gramsInputValue = preconfiguredFood.quantity || "1";
        timeValue = preconfiguredFood.intake_time || preconfiguredFood.time || "";
        quantityType = preconfiguredFood.quantity_type || "g";
      } else {
        ({ activeFoodItem, gramsInputValue, timeValue, quantityType } = foodState);
      }
      
      if (!activeFoodItem || !gramsInputValue) {
        if (!silent) {
          setAlertMessage?.("Please enter a valid quantity.");
          setAlertType?.("warning");
        }
        return false;
      }

      // Use different ID based on context
      const optionId = findOptionId(mealOptionIds, selectedOptionTab);
      if (!optionId) {
        if (!silent) {
          setAlertMessage?.("Meal option is not available. Please try again.");
          setAlertType?.("error");
        }
        return false;
      }

      // Resolve quantity_type_id only from dietCommonUtils (diets API) by unit name — same for template and plan. Never use unitToIdMap or preconfiguredFood.quantity_type_id.
      const quantity_type_id = matchQuantityTypeId(
        quantityType,
        dietCommonUtils?.quantity_types
      );
      if (!quantity_type_id) {
        if (!silent) {
          setAlertMessage?.(
            "Could not map the unit to a quantity type. Ensure the unit exists in diet settings."
          );
          setAlertType?.("error");
        }
        return false;
      }

      let payload;
      const recipeId = getMealFoodId(activeFoodItem);
      const quantityNum = Number(gramsInputValue);
      const formattedTime = formatIntakeTimeForApi(timeValue);
      
      if (context === "plan") {
        // Use target plan API structure
        payload = {
          event_type_id: eventTypeId,
          meal_option_foods: [
            {
              operation: "ADD",
              target_plan_meal_option_id: optionId,
              food_id: recipeId,
              quantity: quantityNum,
              quantity_type_id,
              intake_time: formattedTime,
            },
          ],
        };
      } else {
        // Use template API structure
        payload = {
          event_type_id: eventTypeId,
          foods: [
            {
              operation: "ADD",
              template_meal_option_id: optionId,
              food_id: recipeId,
              quantity: quantityNum,
              quantity_type_id,
              intake_time: formattedTime, // Use same format for consistency
            },
          ],
        };
      }

      const res =
        context === "plan"
          ? await modifyTargetPlanMealOptionFoods(payload)
          : await modifyTemplateMealOptionFoods(payload);

      if (res?.success) {
        if (!silent) {
          setAlertMessage?.("Food item added successfully.");
          setAlertType?.("success");
        }

        loadedOptionsRef.current.delete(selectedOptionTab);

        // Return the envelope, not a bare true: it carries the new row id at
        // data.foods[].id, which callers need to reconcile their optimistic row.
        // Failure still returns false, so existing truthiness checks hold.
        return res;
      }
      await handleQuantityTypeApiError(res, clearDietCommonUtilsCache, fetchDietCommonUtils);
      // The server keys foods per option and rejects a second ADD of the same
      // food ("Food with key ... exists"). Flag it so the caller can convert
      // the add into an update of the existing row instead of surfacing an
      // error for something that is really a quantity change.
      if (/exists/i.test(res?.message || "")) {
        return { success: false, duplicate: true, message: res?.message };
      }
      if (!silent) {
        setAlertMessage?.(res?.message || "Failed to add food item.");
        setAlertType?.("error");
      }
      return false;
    },
    [
      foodState,
      mealOptionIds,
      dietCommonUtils,
      clearDietCommonUtilsCache,
      fetchDietCommonUtils,
      setAlertMessage,
      setAlertType,
      context,
      eventTypeId,
    ]
  );

  const updateFood = useCallback(
    async ({ selectedOptionTab, updatedFood }) => {
      const optionId = findOptionId(mealOptionIds, selectedOptionTab);

      if (!optionId) {
        setAlertMessage?.("Meal option not found.");
        setAlertType?.("error");
        return false;
      }

      // Resolve quantity_type_id only from dietCommonUtils (diets API) by unit name — same for template and plan.
      const quantity_type_id = matchQuantityTypeId(
        updatedFood.quantity_type,
        dietCommonUtils?.quantity_types
      );
      if (!quantity_type_id) {
        setAlertMessage?.("Quantity type invalid. Ensure the unit exists in diet settings.");
        setAlertType?.("error");
        return false;
      }

      let payload;
      if (context === "plan") {
        payload = {
          event_type_id: eventTypeId,
          meal_option_foods: [
            {
              operation: "UPDATE",
              id: updatedFood.deleteId,
              target_plan_meal_option_id: optionId,
              food_id: getMealFoodId(updatedFood),
              quantity: Number(updatedFood.quantity),
              quantity_type_id,
              intake_time: formatIntakeTimeForApi(updatedFood.intake_time),
            },
          ],
        };
      } else {
        payload = {
          event_type_id: eventTypeId,
          foods: [
            {
              operation: "UPDATE",
              id: updatedFood.deleteId,
              template_meal_option_id: optionId,
              food_id: getMealFoodId(updatedFood),
              quantity: Number(updatedFood.quantity),
              quantity_type_id,
              intake_time: formatIntakeTimeForApi(updatedFood.intake_time),
            },
          ],
        };
      }

      const res =
        context === "plan"
          ? await modifyTargetPlanMealOptionFoods(payload)
          : await modifyTemplateMealOptionFoods(payload);

      if (res?.success) {
        // optimistic UI update
        setSavedFoodItems((prev) => {
          const list = prev[selectedOptionTab] || [];
          const next = list.map((it) => {
            if ((it.deleteId || it.id) !== updatedFood.deleteId) return it;
            // Honour an explicit null so a cleared time actually clears.
            const intake_time =
              updatedFood.intake_time !== undefined ? updatedFood.intake_time : it.intake_time;
            const detail = getRecipeDetails(it);
            const quantity = Number(updatedFood.quantity);
            const unitName = updatedFood.quantity_type || it.quantity_type;
            return {
              ...it,
              quantity,
              quantity_type: unitName,
              quantity_type_id: quantity_type_id || it.quantity_type_id || null,
              unitToIdMap: updatedFood.unitToIdMap || it.unitToIdMap || {},
              intake_time,
              scaled_nutrition: resolveScaledNutrition(
                { quantity, quantity_type: unitName },
                detail,
                quantity,
                unitName
              ),
              _updated: Date.now(),
            };
          });
          return { ...prev, [selectedOptionTab]: next };
        });
        setAlertMessage?.("Food Updated Successfully!");
        setAlertType?.("success");
        return res;
      }
      await handleQuantityTypeApiError(res, clearDietCommonUtilsCache, fetchDietCommonUtils);
      setAlertMessage?.(res?.message || "Failed to update food item.");
      setAlertType?.("error");
      return false;
    },
    [
      mealOptionIds,
      dietCommonUtils,
      clearDietCommonUtilsCache,
      fetchDietCommonUtils,
      setAlertMessage,
      setAlertType,
      context,
      eventTypeId,
    ]
  );

  const deleteFood = useCallback(
    async ({ selectedOptionTab, deleteId, silent = false }) => {
      const optionId = findOptionId(mealOptionIds, selectedOptionTab);
      if (!optionId) {
        if (!silent) {
          setAlertMessage?.("Meal option not found.");
          setAlertType?.("error");
        }
        return false;
      }

      let payload;
      if (context === "plan") {
        payload = {
          event_type_id: eventTypeId,
          meal_option_foods: [
            {
              operation: "DELETE",
              id: deleteId,
              // Every other plan payload scopes by option; a DELETE without it
              // could fail to match the row server-side.
              target_plan_meal_option_id: optionId,
            },
          ],
        };
      } else {
        payload = {
          event_type_id: eventTypeId,
          foods: [
            {
              operation: "DELETE",
              id: deleteId,
              template_meal_option_id: optionId,
            },
          ],
        };
      }

      const res =
        context === "plan"
          ? await modifyTargetPlanMealOptionFoods(payload)
          : await modifyTemplateMealOptionFoods(payload);

      if (res?.success) {
        rememberDeleted(deleteId);
        setSavedFoodItems((prev) => {
          const list = prev[selectedOptionTab] || [];
          const next = list.filter((it) => (it.deleteId || it.id) !== deleteId);
          return { ...prev, [selectedOptionTab]: next };
        });
        if (!silent) {
          setAlertMessage?.("Food item deleted successfully.");
          setAlertType?.("success");
        }
        return res;
      }
      if (!silent) {
        setAlertMessage?.(res?.message || "Failed to delete food item.");
        setAlertType?.("error");
      }
      return false;
    },
    [mealOptionIds, setAlertMessage, setAlertType, context, eventTypeId, rememberDeleted]
  );

  // ---------- UI helpers ----------
  const handleFoodItemClick = useCallback(
    async (foodItem, openInnerModal) => {
      const id = getMealFoodId(foodItem);
      const detail = getRecipeDetails(foodItem);
      if (!id || !detail) {
        setAlertMessage?.("Invalid recipe selected.");
        setAlertType?.("warning");
        return;
      }
      openInnerModal(
        normalizeDetailForModal({
          ...detail,
          recipe_id: id,
          recipe_name: getRecipeName(foodItem),
        })
      );
    },
    [
      normalizeDetailForModal,
      setAlertMessage,
      setAlertType,
    ]
  );

  const calculatedValues = useMemo(
    () =>
      calculateNutritionalValues(
        foodState.gramsInputValue,
        foodState.activeFoodItem,
        foodState.quantityType
      ),
    [foodState, calculateNutritionalValues]
  );

  const addMealOption = useCallback(
    async (periodName, mealIdMap, mealOptionIds, mealIdOverride) => {
      if (!periodName || !mealIdMap) {
        setAlertMessage?.("Invalid parameters for adding option.");
        setAlertType?.("error");
        return false;
      }

      // resolve meal id (prefer explicit override)
      const lower = String(periodName).trim().toLowerCase();
      const meal_id = mealIdOverride || mealIdMap[lower];
      if (!meal_id) {
        setAlertMessage?.("Could not resolve meal id for this period.");
        setAlertType?.("error");
        return false;
      }

      // collect existing titles (case-insensitive keys)
      let existing = {};
      if (mealOptionIds && typeof mealOptionIds === "object") {
        existing = mealOptionIds[periodName] || {};
        if (!Object.keys(existing).length) {
          const k = Object.keys(mealOptionIds).find(
            (p) => p && p.toLowerCase() === lower
          );
          if (k) existing = mealOptionIds[k] || {};
        }
      }
      const existingTitles = new Set(
        Object.keys(existing).map((t) => String(t).trim().toLowerCase())
      );

      // helper to find next available number from a starting seed
      const nextAvailableTitle = (seedNum) => {
        let n = Math.max(1, seedNum);
        while (existingTitles.has(`option ${n}`.toLowerCase())) n += 1;
        return `Option ${n}`;
      };

      // start from the max numeric title we see, then probe upward
      const maxNum = Object.keys(existing).reduce((acc, title) => {
        const n = parseInt(optionNumber(title), 10);
        return Number.isFinite(n) ? Math.max(acc, n) : acc;
      }, 0);

      // up to 10 attempts in case server knows about more duplicates than our UI
      let attempt = 0;
      let title = nextAvailableTitle(maxNum + 1);

      while (attempt < 10) {
        const payload =
          context === "plan"
            ? {
                event_type_id: eventTypeId,
                meal_options: [
                  {
                    operation: "ADD",
                    target_plan_meal_id: meal_id,
                    title,
                    description: "",
                  },
                ],
              }
            : {
                event_type_id: eventTypeId,
                meal_options: [
                  {
                    operation: "ADD",
                    template_meal_id: meal_id,
                    title,
                    description: "",
                  },
                ],
              };

        try {
          const res =
            context === "plan"
              ? await modifyTargetPlanMealOptions(payload)
              : await modifyTemplateMealOptions(payload);

          if (res?.success) {
            setAlertMessage?.(`${title} added successfully!`);
            setAlertType?.("success");
            return true;
          }

          // if server says title exists, bump and retry
          const msg = String(res?.message || res?.detail || "").toLowerCase();
          if (msg.includes("title") && msg.includes("exists")) {
            existingTitles.add(title.toLowerCase());
            title = nextAvailableTitle(
              (parseInt(title.replace(/[^0-9]/g, ""), 10) || 1) + 1
            );
            attempt += 1;
            continue;
          }

          setAlertMessage?.(res?.message || "Failed to add option.");
          setAlertType?.("error");
          return false;
        } catch (err) {
          const msg = String(err?.message || "").toLowerCase();
          if (msg.includes("title") && msg.includes("exists")) {
            existingTitles.add(title.toLowerCase());
            title = nextAvailableTitle(
              (parseInt(title.replace(/[^0-9]/g, ""), 10) || 1) + 1
            );
            attempt += 1;
            continue;
          }
          console.error("Error adding meal option:", err);
          setAlertMessage?.("Failed to add option.");
          setAlertType?.("error");
          return false;
        }
      }

      setAlertMessage?.("Could not find a free option title. Try again.");
      setAlertType?.("error");
      return false;
    },
    [context, eventTypeId, setAlertMessage, setAlertType]
  );

  return {
    savedFoodItems,
    setSavedFoodItems,
    foodState,
    refreshTemplateFoods,
    setFoodState,
    calculatedValues,

    // exported behaviors
    ensureOptionPlaceholder,
    loadOptionFromTemplateOnce,
    handleFoodItemClick,
    addFood,
    updateFood,
    deleteFood,
    refreshPlanFoods, // NEW: for plan context
    addMealOption,
    fetchDetailIfNeeded,
    normalizeDetailForModal,
  };
}
